import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Sponsors } from "../components/Sponsors";
import { api, HERO_IMG, GALLERY_IMGS, STADIUM_GALLERY, RB100_LOGO, RB100_LOGO_PNG, VENUE, ABOUT_IMG } from "../lib/api";
import { Calendar, MapPin, Trophy, ChevronLeft, ExternalLink, Clock, Users, Car as CarIcon, LogOut as LogOutIcon } from "lucide-react";

export default function Home() {
  return (
    <div data-testid="home-page" className="min-h-screen bg-white text-rb-ink">
      <Navbar />

      {/* Hero — kept dramatic with dark image */}
      {/* Hero — Image background with light text overlay */}
      <section className="relative min-h-[100svh] sm:h-[90vh] sm:min-h-[640px] overflow-hidden bg-black flex flex-col">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-bottom opacity-90"
        />
        {/* Soft overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-l from-black/30 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        <div className="absolute inset-0 rb-noise opacity-25" />

        <div className="relative max-w-7xl mx-auto px-6 flex-1 flex flex-col justify-end pt-16 pb-12 sm:pb-20 w-full">
          <div className="animate-fade-up">
            {/* Big Logo (transparent PNG, no background) */}
            <div className="h-44 w-44 sm:h-56 sm:w-56 flex items-center justify-center mb-8">
              <img
                src={RB100_LOGO_PNG}
                alt="RB100 — Since 2002"
                className="h-full w-full object-contain drop-shadow-2xl"
              />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[2px] w-12 bg-rb-red" />
              <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
                CLUB MEMBERSHIP · RB100
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight max-w-4xl text-white"
              style={{ color: "#fff" }}
            >
              سجّل سيارتك في
              <br />
              <span className="text-rb-red">فريق RB100</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
              <span className="text-white font-bold">للمشاركة في الفعاليات القادمة!</span>
              <br />
              خليك أوّل واحد، سجّل من الآن وكن جاهزاً لأقوى التجمعات والفعاليات مع نادي RB100 — منذ ٢٠٠٢ ولليوم 🏁
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="hero-cta-register"
                className="group bg-rb-red hover:bg-rb-redHover text-white font-bold px-8 py-4 transition-colors flex items-center gap-3 text-base"
              >
                سجّل عضويتك
                <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              </Link>
              <a
                href="#contact"
                className="border border-white/40 hover:border-rb-red hover:text-rb-red text-white font-medium px-8 py-4 transition-colors text-base"
              >
                تواصل مع النادي
              </a>
            </div>
          </div>
        </div>

        {/* Bottom info strip — flows below content on mobile, absolute on desktop */}
        <div className="relative sm:absolute bottom-0 inset-x-0 border-t border-white/15 bg-black/85 backdrop-blur">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x sm:divide-x-reverse divide-white/15">
            <div className="px-6 py-5 flex items-center gap-4">
              <Calendar className="w-6 h-6 text-rb-red shrink-0" />
              <div>
                <div className="text-[10px] tracking-[0.25em] text-white/60 uppercase">منذ</div>
                <div className="font-bold text-base text-white">عام ٢٠٠٢</div>
              </div>
            </div>
            <div className="px-6 py-5 flex items-center gap-4">
              <MapPin className="w-6 h-6 text-rb-red shrink-0" />
              <div>
                <div className="text-[10px] tracking-[0.25em] text-white/60 uppercase">المدينة</div>
                <div className="font-bold text-base text-white">المدينة المنورة</div>
              </div>
            </div>
            <div className="px-6 py-5 flex items-center gap-4">
              <Trophy className="w-6 h-6 text-rb-red shrink-0" />
              <div>
                <div className="text-[10px] tracking-[0.25em] text-white/60 uppercase">الإرث</div>
                <div className="font-bold text-base text-white">٢٣ عاماً من الشغف</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About — Dark theme */}
      <section id="about" className="py-24 md:py-32 bg-black text-white">
       <div className="max-w-7xl mx-auto px-6"> <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src={ABOUT_IMG}
              alt="تجمع RB100"
              className="w-full h-[480px] object-cover"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-rb-red" />
              <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
                ABOUT THE CLUB
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-6 text-white">
              عن نادي RB100
            </h2>
            <p className="text-white/75 leading-loose text-base mb-5">
              تأسّس نادي <span className="text-rb-red font-bold">RB100</span> عام
              <span className="text-white font-bold"> ٢٠٠٢ </span>
              في المدينة المنورة، ليكون بيتاً لعشّاق السيارات الكلاسيكية والمعدّلة.
              على مدى أكثر من عقدين، نظّم النادي عشرات التجمعات والفعاليات،
              وجمع نخبة من أجمل السيارات وأكثرها فرادة. شغفنا الحقيقي هو الحفاظ
              على ذاكرة السيارات وإبقاء روح الكلاسيك حيّة.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10 pt-8 border-t border-white/15">
              <div>
                <div className="font-heading text-4xl font-extrabold text-rb-red">23+</div>
                <div className="text-xs tracking-widest text-white/60 uppercase mt-1">عاماً من النشاط</div>
              </div>
              <div>
                <div className="font-heading text-4xl font-extrabold text-rb-red">50+</div>
                <div className="text-xs tracking-widest text-white/60 uppercase mt-1">تجمع منظَّم</div>
              </div>
              <div>
                <div className="font-heading text-4xl font-extrabold text-rb-red">100+</div>
                <div className="text-xs tracking-widest text-white/60 uppercase mt-1">عضو وشغف</div>
              </div>
            </div>
          </div>
        </div>
       </div>
      </section>

      {/* Achievements / Highlights */}
      <section className="bg-white py-20 sm:py-24 border-b border-rb-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="h-[2px] w-10 bg-rb-red" />
            <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
              ACHIEVEMENTS · إنجازات
            </span>
            <span className="h-[2px] w-10 bg-rb-red" />
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center mb-14 text-rb-ink">
            أبرز إنجازات النادي
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                year: "2002",
                title: "تأسيس النادي",
                desc: "انطلاق نادي RB100 في المدينة المنورة كأول تجمّع مخصص لعشّاق الكلاسيك والمعدّلة في المنطقة.",
              },
              {
                year: "2024",
                title: "تجمع الاستاد الرياضي",
                desc: "تجمع كبير في الاستاد الرياضي بالمدينة المنورة، وثّق حضوراً قوياً لأبرز السيارات الكلاسيكية في المنطقة.",
              },
              {
                year: "2026",
                title: "شتوية هوى",
                desc: "بالشراكة مع فريق شباب المدينة في مركز هوا الترفيهي، فعالية امتدت ٣ أيام (٢٩ إبريل - ١ مايو).",
              },
              {
                year: "—",
                title: "شراكات النادي",
                desc: "علاقات راسخة مع رعاة من القطاعين الحكومي (وزارة الرياضة) والخاص (نورين، 6 أيام، اطلب قطعك).",
              },
              {
                year: "—",
                title: "توثيق الذاكرة",
                desc: "أرشيف بصري ضخم يضمّ أبرز التجمعات على مدى ٢٣ عاماً، يبقى مرجعاً لعشّاق السيارات في المملكة.",
              },
              {
                year: "—",
                title: "هوية مميّزة",
                desc: "بناء هوية بصرية عريقة جعلت من شعار RB100 مرجعاً يُعرف به النادي ومحبّوه.",
              },
            ].map((a) => (
              <div
                key={a.title}
                className="bg-rb-surface border border-rb-border p-6 hover:border-rb-red transition-colors"
              >
                <div className="text-rb-red font-mono font-bold text-sm tracking-widest mb-3">{a.year}</div>
                <div className="font-heading font-extrabold text-lg text-rb-ink mb-2">{a.title}</div>
                <p className="text-sm text-rb-textMuted leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery — two collections */}
      <section id="gallery" className="bg-white py-24 border-y border-rb-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-rb-red" />
              <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
                GALLERY · المعرض
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-rb-ink">
              لقطات من تجمعات النادي
            </h2>
          </div>

          {/* Hawa Park — Latest */}
          <div className="mb-12">
            <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-rb-border">
              <h3 className="font-heading text-xl font-bold text-rb-ink">
                شتوية هوى — مركز هوا الترفيهي
              </h3>
              <span className="text-xs tracking-widest text-rb-textMuted uppercase">2026 · الأحدث</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-1">
              {GALLERY_IMGS.map((src) => (
                <div
                  key={src}
                  className="relative h-[260px] sm:h-[300px] lg:h-[340px] overflow-hidden group bg-black"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              ))}
            </div>
          </div>

          {/* Stadium — Older */}
          <div>
            <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-rb-border">
              <h3 className="font-heading text-xl font-bold text-rb-ink">
                تجمع الاستاد الرياضي بالمدينة المنورة
              </h3>
              <span className="text-xs tracking-widest text-rb-textMuted uppercase">أرشيف</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1">
              {STADIUM_GALLERY.map((src) => (
                <div
                  key={src}
                  className="relative h-[180px] sm:h-[200px] lg:h-[220px] overflow-hidden group bg-black"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover opacity-90 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <ContactSection />

      <Sponsors />

      <Footer />
    </div>
  );
}

/* ===================== Contact Section ===================== */
function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("يرجى تعبئة الاسم والرسالة على الأقل");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.mobile) delete payload.mobile;
      if (!payload.subject) delete payload.subject;
      await api.post("/messages", payload);
      setDone(true);
      toast.success("تم إرسال رسالتك. سنرد عليك في أقرب وقت ✨");
      setForm({ name: "", email: "", mobile: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("تعذّر إرسال الرسالة. حاول مرة أخرى لاحقاً.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-rb-surface py-24 border-t border-rb-border">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mx-auto bg-white p-3 h-32 w-32 flex items-center justify-center mb-8 border border-rb-border shadow-md">
          <img src={RB100_LOGO} alt="RB100 — Since 2002" className="h-full w-full object-contain" />
        </div>
        <div className="flex items-center gap-3 mb-4 justify-center">
          <span className="h-[2px] w-10 bg-rb-red" />
          <span className="text-xs tracking-[0.4em] text-rb-red font-bold">CONTACT · تواصل</span>
          <span className="h-[2px] w-10 bg-rb-red" />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center mb-3 text-rb-ink">
          تواصل مع نادي RB100
        </h2>
        <p className="text-center text-rb-textMuted mb-10">
          سواء كنت ترغب في الانضمام للنادي أو تنسيق فعالية أو طرح أي استفسار — راسلنا مباشرة.
        </p>

        {done ? (
          <div data-testid="contact-success" className="bg-green-50 border border-green-200 text-green-900 p-8 text-center">
            <div className="font-heading font-extrabold text-2xl mb-2">تم استلام رسالتك ✓</div>
            <p className="text-sm">سنتواصل معك في أقرب وقت ممكن. شكراً لتواصلك مع النادي.</p>
            <button
              onClick={() => setDone(false)}
              data-testid="contact-send-another"
              className="mt-6 inline-block text-rb-red font-bold text-sm hover:underline"
            >
              إرسال رسالة أخرى
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-rb-border p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <ContactField label="الاسم *" testid="contact-field-name">
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-input-name"
                  required
                  maxLength={120}
                  className="w-full bg-rb-surface border border-rb-border px-4 py-3 text-rb-ink focus:border-rb-red focus:outline-none transition-colors"
                />
              </ContactField>
              <ContactField label="رقم الجوال (اختياري)" testid="contact-field-mobile">
                <input
                  type="tel"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  data-testid="contact-input-mobile"
                  dir="ltr"
                  placeholder="+9665XXXXXXXX"
                  className="w-full bg-rb-surface border border-rb-border px-4 py-3 text-rb-ink focus:border-rb-red focus:outline-none transition-colors text-right"
                />
              </ContactField>
              <ContactField label="البريد الإلكتروني (اختياري)" testid="contact-field-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-input-email"
                  dir="ltr"
                  className="w-full bg-rb-surface border border-rb-border px-4 py-3 text-rb-ink focus:border-rb-red focus:outline-none transition-colors text-right"
                />
              </ContactField>
              <ContactField label="الموضوع (اختياري)" testid="contact-field-subject">
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-input-subject"
                  maxLength={200}
                  placeholder="مثال: انضمام للنادي"
                  className="w-full bg-rb-surface border border-rb-border px-4 py-3 text-rb-ink focus:border-rb-red focus:outline-none transition-colors"
                />
              </ContactField>
            </div>
            <ContactField label="الرسالة *" testid="contact-field-message">
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-input-message"
                required
                rows={6}
                maxLength={2000}
                className="w-full bg-rb-surface border border-rb-border px-4 py-3 text-rb-ink focus:border-rb-red focus:outline-none transition-colors resize-y"
                placeholder="اكتب رسالتك هنا..."
              />
            </ContactField>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full bg-rb-red hover:bg-rb-redHover disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-8 py-4 transition-colors flex items-center justify-center gap-3 text-base"
            >
              {submitting ? "جارٍ الإرسال..." : "إرسال الرسالة"}
              {!submitting && <ChevronLeft className="w-5 h-5" />}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

const ContactField = ({ label, testid, children }) => (
  <label data-testid={testid} className="block">
    <span className="block text-sm font-bold text-rb-ink mb-2">{label}</span>
    {children}
  </label>
);
